import { UseQueryOptions, UseQueryResult, skipToken, useQuery } from '@tanstack/react-query';

import { AuthenticationScheme, useAssertFetchPermitted } from 'features/auth';

/**
 * Options for the API query hook
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface UseApiQueryOptions<TData = any> extends Omit<UseQueryOptions<TData>, 'queryKey' | 'queryFn'> {
  /** Base key for the query cache */
  queryKey: (string | number | boolean | null | undefined)[];
  /** API function to call */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiFn: (...args: any[]) => Promise<TData>;
  /** Parameters to pass to the API function */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: any[];
  /** Required authentication schemes - auto-detected if not specified */
  requiredSchemes?: AuthenticationScheme[];
  /** Whether to skip the query if any parameter is null/undefined */
  skipIfInvalidParams?: boolean;
  /** Custom condition to skip the query */
  skipCondition?: () => boolean;
  /** Enable debug logging for this query */
  debug?: boolean;
}

/**
 * TanStack Query hook for API calls with automatic auth handling
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApiQuery<TData = any>({
  queryKey,
  apiFn,
  params,
  requiredSchemes = [AuthenticationScheme.OIDC, AuthenticationScheme.SHARE],
  skipIfInvalidParams = false,
  skipCondition,
  staleTime = 1000 * 60 * 5, // Default 5 minutes
  debug = false,
  ...queryOptions
}: UseApiQueryOptions<TData>): UseQueryResult<TData> {
  const activeScheme = useAssertFetchPermitted(requiredSchemes);

  // Use params as-is since HttpClient will auto-detect auth mode
  const finalParams = params;

  // Check if we should skip the query
  const shouldSkip = () => {
    // Custom skip condition
    if (skipCondition?.()) {
      if (debug) console.log(`[useApiQuery] ${apiFn.name} skipped: custom condition`);
      return true;
    }

    // Skip if authentication not ready
    if (activeScheme == null) {
      if (debug) console.log(`[useApiQuery] ${apiFn.name} skipped: no auth`);
      return true;
    }

    // Skip if any parameter is invalid
    if (skipIfInvalidParams && finalParams) {
      const hasInvalidParam = finalParams.some((param) => param === null || param === undefined || (typeof param === 'string' && param.trim() === ''));
      if (hasInvalidParam) {
        if (debug) console.log(`[useApiQuery] ${apiFn.name} skipped: invalid params`, finalParams);
        return true;
      }
    }

    return false;
  };

  const skip = shouldSkip();

  if (debug) {
    console.log(`[useApiQuery] ${apiFn.name}:`, {
      params: finalParams,
      activeScheme,
      skip,
      willExecute: !skip,
    });
  }

  return useQuery({
    queryKey: [...queryKey, ...finalParams],
    queryFn: skip
      ? skipToken
      : async () => {
          if (debug) {
            console.log(`[useApiQuery] Executing ${apiFn.name}:`, finalParams);
          }
          const result = await apiFn(...finalParams);
          if (debug) {
            console.log(`[useApiQuery] ${apiFn.name} result:`, result);
          }
          return result;
        },
    staleTime,
    ...queryOptions,
  });
}
